import React from 'react'
import { Box } from '@mui/material'
import { SocialChart } from '../social-media/SocialChart'
import { SOCIAL_COLOR_MAP, EMOTION_GRADIENTS } from '../social-media/design-tokens'
import type { ChartDefinition } from '../social-media/chart-types'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

/** The two chart heights the dashboards use. Emotion is 15% taller than the
 *  rest because its category axis carries the most labels and clipped labels
 *  were the original reason for the difference. */
export const CHART_HEIGHT = 360
export const TALL_CHART_HEIGHT = 414

export interface CarbonChartProps {
  /** What to draw and how — the same definition the hook fetched with. */
  definition: ChartDefinition | undefined
  data: PlatinumData | null | undefined
  isLoading?: boolean
  /** Box height in px. Defaults to CHART_HEIGHT. */
  height?: number
  /** Solid series colours (sentiment/emotion bars) vs gradients (pies). */
  useGradients?: boolean
  hideLegend?: boolean
  /** A fetch error to show in place of the chart. */
  error?: string | null
  /** Series colours by lower-cased name; defaults to the social codes' colours. */
  colorMap?: Record<string, string>
}

/**
 * One crosstab chart at a fixed height.
 *
 * It is a thin wrapper over `<SocialChart>` and that is the entire point: the
 * original page repeated the same six-argument `renderChart(id, fill, gradients,
 * hideLegend)` closure plus a `<Box sx={{height}}>` at every call site, so the
 * height and colour conventions lived in a local helper that nothing else could
 * reuse. Here they are defaults on a component any page can mount.
 *
 * It renders nothing when the definition is missing rather than throwing. A
 * page that names a chart the hook was not given is a mistake, but it is a
 * layout mistake — it should show as one missing panel, not a blank dashboard.
 */
export const CarbonChart: React.FC<CarbonChartProps> = ({
  definition,
  data,
  isLoading = false,
  height = CHART_HEIGHT,
  useGradients = true,
  hideLegend = false,
  error = null,
  colorMap = SOCIAL_COLOR_MAP,
}) => {
  if (!definition) return null

  return (
    <Box sx={{ height }}>
      <SocialChart
        definition={definition}
        data={data ?? null}
        isLoading={isLoading}
        fillContainer
        colorMap={colorMap}
        gradientMap={useGradients ? EMOTION_GRADIENTS : undefined}
        hideLegend={hideLegend}
        error={error}
      />
    </Box>
  )
}

export default CarbonChart
